// Asset type / folder categorisation. The list of types is fetched from the API
// (GET v1/asset-types) and mapped into the FE { key, label } shape below; the
// per-service-group folder lists live here. The chosen type is persisted on the asset
// itself (asset.assetTypeId / asset.assetType) and read back from there — both are
// nullable, so we fall back to the file type / service group when absent.

// AssetType.ClientGenerated — the client AI Studio's saved output. The type marks client origin;
// AI-ness itself is the ai_request_id blob flag, which these assets also carry.
export const CLIENT_GENERATED_ASSET_TYPE_ID = 9

// AssetType.IGuideUrl — assets that carry a direct tour URL (e.g. Virtual_Tour) instead of a file.
export const I_GUIDE_URL_ASSET_TYPE_ID = 10

// Map the API asset types into a { key, label, id } lookup keyed by the lowercased name
// (e.g. { id: 1, name: 'Image' } -> image: { key: 'image', label: 'Image', id: 1 }).
const buildAssetTypeMap = (apiAssetTypes = []) =>
  apiAssetTypes.reduce((map, { id, name }) => {
    const key = name.toLowerCase()
    map[key] = { key, label: name, id }
    return map
  }, {})

// Folders available per service group (Copywriting intentionally excluded).
const serviceGroupAssetTypes = {
  Photography: ['image', 'cropped', 'stitched', 'resized', 'others'],
  'Virtual Staging': ['image', 'cropped', 'stitched', 'resized', 'others'],
  Floorplan: ['pdf', 'others'],
  Video: ['video', 'others'],
  VidMo: ['video', 'others'],
}

// Ordered list of { key, label, id } for a service group, or [] when not applicable.
// apiAssetTypes is the list returned by GET v1/asset-types; keys it doesn't include
// are skipped (so the options stay empty until the types have loaded).
export function assetTypesForServiceGroup(serviceGroupName, apiAssetTypes = []) {
  const assetTypes = buildAssetTypeMap(apiAssetTypes)
  return (serviceGroupAssetTypes[serviceGroupName] || []).map(key => assetTypes[key]).filter(Boolean)
}

// Resolve an asset's folder key: use the asset's own assetType when present, otherwise
// fall back to the file type / service group so assets without a type still bucket sensibly.
export function deriveAssetTypeKey(content, serviceGroupName) {
  const allowed = serviceGroupAssetTypes[serviceGroupName] || []

  const assetTypeKey = content?.assetType?.name?.toLowerCase()
  if (assetTypeKey && allowed.includes(assetTypeKey)) {
    return assetTypeKey
  }

  const fileType = content?.file?.fileType?.name?.toLowerCase()

  if (fileType === 'pdf' && allowed.includes('pdf')) return 'pdf'
  if (['mp4', 'mov'].includes(fileType) && allowed.includes('video')) return 'video'
  if (['jpg', 'jpeg', 'png', 'dng', 'cr2', 'arw'].includes(fileType) && allowed.includes('image')) return 'image'

  return allowed.includes('others') ? 'others' : (allowed[0] || 'others')
}
